import React, { useState } from 'react';
import styled from 'styled-components';
import { ScheduleButton } from './ActionButtonsStyles';
import Modal, { ConsultationForm } from './Modal';
import { useNavigate } from 'react-router-dom';

// Container estilizado para o botão de agendamento
const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 3rem auto 2rem;
  position: relative;
  z-index: 5;
  
  /* Efeito de destaque ao redor do botão */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 340px;
    height: 100px;
    background: radial-gradient(
      circle at center,
      rgba(0, 255, 255, 0.15) 0%,
      rgba(64, 128, 255, 0.08) 50%,
      rgba(64, 128, 255, 0.04) 70%,
      rgba(64, 128, 255, 0.01) 85%,
      transparent 100%
    );
    border-radius: 50px;
    z-index: -1;
    filter: blur(8px);
    opacity: 0.7;
    will-change: opacity, filter;
    animation: buttonHighlight 8s infinite cubic-bezier(0.4, 0.0, 0.2, 1);
  }
  
  /* Linhas tecnológicas */
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 1px;
    background: linear-gradient(to right, rgba(0, 255, 255, 0), rgba(0, 255, 255, 0.6), rgba(0, 255, 255, 0));
    filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.5));
  }
  
  @keyframes buttonHighlight {
    0% { opacity: 0.5; filter: blur(8px); }
    10% { opacity: 0.55; filter: blur(7.8px); }
    25% { opacity: 0.65; filter: blur(7px); }
    40% { opacity: 0.75; filter: blur(6.5px); }
    50% { opacity: 0.8; filter: blur(6px); }
    60% { opacity: 0.75; filter: blur(6.5px); }
    75% { opacity: 0.65; filter: blur(7px); }
    85% { opacity: 0.58; filter: blur(7.5px); }
    90% { opacity: 0.55; filter: blur(7.8px); }
    95% { opacity: 0.52; filter: blur(7.9px); }
    100% { opacity: 0.5; filter: blur(8px); }
  }
  
  @keyframes dataFlow {
    0% { transform: translateY(0); opacity: 0; }
    20% { opacity: 0.8; }
    80% { opacity: 0.8; }
    100% { transform: translateY(-20px); opacity: 0; }
  }
`;

// Componente para o botão de agendamento
const ScheduleButtonSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // Função para abrir o modal de agendamento
  const openScheduleModal = () => {
    setShowModal(true);
  };
  
  // Função para fechar o modal
  const closeModal = () => {
    setShowModal(false);
  };
  
  // Função para lidar com o envio do formulário
  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      console.log('Dados do agendamento:', formData);
      
      // O salvamento já foi feito pelo webhook, não precisamos salvar novamente
      // Apenas fechamos o modal e redirecionamos
      
      // Fechamos o modal
      closeModal();
      
      // Redirecionamos para a página de agradecimento
      navigate('/ty');
    } catch (error) {
      console.error('Erro ao processar agendamento:', error);
      alert('Ocorreu um erro ao agendar sua consultoria. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  // Renderiza as partículas de dados para o botão de agendamento
  const renderButtonParticles = () => {
    const particles = [];
    for (let i = 0; i < 8; i++) {
      const top = `${Math.random() * 100}%`;
      const left = `${Math.random() * 100}%`;
      const delay = `${Math.random() * 2}s`;
      const size = `${Math.random() * 2 + 2}px`;
      
      particles.push(
        <div 
          key={i}
          className="data-particle"
          style={{
            position: 'absolute',
            width: size,
            height: size,
            backgroundColor: '#00FFFF',
            borderRadius: '50%',
            top,
            left,
            opacity: 0,
            animation: `dataFlow 2s infinite`,
            animationDelay: delay,
            boxShadow: '0 0 3px #00FFFF',
            zIndex: 1
          }}
        />
      );
    }
    return particles;
  };

  return (
    <ButtonContainer>
      <ScheduleButton onClick={openScheduleModal}>
        <svg 
          className="calendar-icon" 
          width="22" 
          height="22" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect 
            x="3" 
            y="4" 
            width="18" 
            height="18" 
            rx="2" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M16 2V6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M8 2V6" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <path 
            d="M3 10H21" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <circle cx="8" cy="14" r="1" fill="currentColor" />
          <circle cx="12" cy="14" r="1" fill="currentColor" />
          <circle cx="16" cy="14" r="1" fill="currentColor" />
          <circle cx="8" cy="18" r="1" fill="currentColor" />
          <circle cx="12" cy="18" r="1" fill="currentColor" />
          <circle cx="16" cy="18" r="1" fill="currentColor" />
        </svg>
        Agendar Consultoria
        {renderButtonParticles()}
      </ScheduleButton>
      
      {/* Modal de Agendamento */}
      <Modal 
        isOpen={showModal} 
        onClose={closeModal} 
        title="Agendar Consultoria"
      >
        <ConsultationForm onSubmit={handleFormSubmit} isSubmitting={loading} />
      </Modal>
    </ButtonContainer>
  );
};

export default ScheduleButtonSection;
